<script setup lang="ts">
import { ref } from 'vue'
import { useDismissablePopover } from '@/composables/useDismissablePopover'

const props = defineProps<{
  /**
   * Extra classes applied to the dropdown panel — use this to control width.
   * Defaults to 'w-36'.
   */
  menuClass?: string
  /**
   * When true, the dropdown is teleported to <body> and positioned with fixed
   * coordinates derived from getBoundingClientRect(). Use this inside table
   * cells where an ancestor has overflow:hidden that would clip an absolute
   * dropdown.
   */
  teleport?: boolean
  /**
   * Accessible label for the trigger button.
   */
  ariaLabel?: string
  /**
   * When true, the trigger is rendered but disabled: it cannot be clicked and
   * the menu never opens. Defaults to false.
   */
  disabled?: boolean
}>()

const triggerRef = ref<HTMLButtonElement | null>(null)
const panelRef = ref<HTMLElement | null>(null)
const menuPosition = ref({ top: 0, left: 0 })

// Closes on Escape and on any click outside the trigger and panel. Opts in to singleOpen so opening
// one menu closes any other open popover (the trigger stops click propagation, so other menus never
// see an outside click).
const popover = useDismissablePopover({
  anchorRef: triggerRef,
  popoverRef: panelRef,
  singleOpen: true,
  closeOnEscape: true,
  closeOnOutside: ['click'],
})
const open = popover.isOpen

function toggle(event: MouseEvent): void {
  if (props.disabled) return
  if (props.teleport && !open.value) {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
    menuPosition.value = {
      top: rect.bottom + 4,
      left: rect.right - 160,
    }
  }
  popover.toggle()
}

function close(): void {
  popover.close()
}

defineExpose({ close })
</script>

<template>
  <div class="relative">
    <button
      ref="triggerRef"
      type="button"
      class="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 transition-colors"
      :class="disabled
        ? 'opacity-40 cursor-not-allowed'
        : 'hover:text-white hover:bg-slate-700/50'"
      :disabled="disabled"
      :aria-expanded="open"
      :aria-label="ariaLabel"
      :title="disabled ? ariaLabel : undefined"
      @click.stop="toggle"
    >
      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 5a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm0 7a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm0 7a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" />
      </svg>
    </button>

    <!-- Standard absolute dropdown (default) -->
    <div
      v-if="open && !teleport"
      ref="panelRef"
      class="absolute right-0 mt-1 rounded-lg bg-slate-800 border border-slate-700 shadow-xl py-1 z-10"
      :class="menuClass ?? 'w-36'"
      @click.stop
    >
      <slot />
    </div>
  </div>

  <!-- Teleported dropdown (for use inside overflow:hidden containers, e.g. tables) -->
  <Teleport v-if="teleport" to="body">
    <div
      v-if="open"
      ref="panelRef"
      class="fixed z-40 rounded-lg bg-slate-800 border border-slate-700 shadow-xl py-1"
      :class="menuClass ?? 'w-36'"
      :style="{ top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }"
      @click.stop
    >
      <slot />
    </div>
  </Teleport>
</template>
